import {
    AnalyticsProvider,
    PageTracker,
    PostHogAnalyticsProvider,
    SentryErrorTracker,
} from '@order-up/analytics';
import { clientApiFeatureFlagsProvider, onAppCheckRefused } from '@order-up/client/vite';
import {
    addBreadcrumb,
    captureException,
    captureMessage,
    flush,
    getClient,
    init,
    setTag,
    setUser,
    startInactiveSpan,
} from '@sentry/react';
import { useState, type ReactNode } from 'react';

const APP_NAME = 'checkout';

/**
 * Module-level guard so `SentryErrorTracker.initializeWithModule(...)` runs
 * exactly once per browser tab regardless of how many `<AnalyticsBoundary>`
 * instances mount (Strict Mode double-invokes effects in dev). Sentry's own
 * `init()` is idempotent at the SDK level, but we keep our own guard so we
 * never load `@sentry/react` twice on a hot reload.
 */
let sentryTracker: SentryErrorTracker | null = null;

/** Every `@sentry/react` member `SentryErrorTracker` calls — and nothing else. */
const SENTRY_TRACKER_API = {
    addBreadcrumb,
    captureException,
    captureMessage,
    flush,
    getClient,
    init,
    setTag,
    setUser,
    startInactiveSpan,
};

// Checkout does NOT reload on an App Check refusal (the storefronts do). Its
// guest runs on the server-minted guest session, which needs no App Check, so
// a reload rescues nothing here and only does damage: it landed mid hand-off,
// re-sent the single-use code and showed a guest "This checkout link has
// expired" (prod 2026-10-12), as it once took POS keyed entry down
// (2026-09-27). test/checkoutNoRefusalReload.test.ts holds this.

/**
 * App Check refusals (a browser whose reCAPTCHA score fell under the project
 * minimum) used to be invisible: nothing threw, the page just never loaded.
 * `refused` is reported when it happens (checkout never reloads for one, so
 * `recovered-after-reload` cannot occur here).
 */
function reportAppCheckRefusals(tracker: SentryErrorTracker): void {
    onAppCheckRefused((event) => {
        tracker.captureMessage(
            event.kind === 'refused'
                ? 'App Check refused this browser'
                : 'App Check recovered after reload',
            event.kind === 'refused' && event.afterReload ? 'error' : 'warning',
            {
                component: 'AppCheck',
                operation: event.kind,
                afterReload: String(event.afterReload),
                willReload: String(event.willReload),
            },
        );
    });
}

function getOrCreateSentryTracker(): SentryErrorTracker {
    if (sentryTracker) return sentryTracker;
    // Inject the DSN/env/release from `import.meta.env.VITE_*`. `process.env`
    // does NOT exist in a Vite client bundle, so without this injection the
    // tracker's DSN is permanently empty and `isConfigured()` stays false —
    // making `captureException` a silent no-op. See AUTH_BOOT_DIAGNOSTIC /
    // Initiative #3 PR 8 for the original (broken) Vite path.
    const tracker = new SentryErrorTracker(APP_NAME, {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment:
            import.meta.env.VITE_VERCEL_ENV
            || import.meta.env.VITE_APP_ENV
            || import.meta.env.MODE,
        release: import.meta.env.VITE_SENTRY_RELEASE,
    });
    if (typeof window !== 'undefined') {
        // `main.tsx` already calls `Sentry.init(...)` directly at module
        // load. `initializeWithModule` detects the live client via
        // `getClient()` and attaches WITHOUT re-initializing.
        //
        // The tracker gets ONLY the functions it calls, never the module
        // namespace. Handing it `import('@sentry/react')` whole made every
        // export reachable, so Session Replay, the Feedback widget and the
        // canvas recorder — none of which checkout enables — rode in the first
        // download (measured 2026-09-28). `main.tsx` imports `@sentry/react`
        // statically anyway, so the dynamic import deferred nothing.
        tracker.initializeWithModule(SENTRY_TRACKER_API);
        reportAppCheckRefusals(tracker);
    }
    sentryTracker = tracker;
    return tracker;
}

/**
 * Vite-side analytics boundary for the checkout app. Mirrors the Next-side
 * `apps/management/src/components/AnalyticsBoundary.tsx` shape. Phase 6
 * Wave 2.
 */
export function AnalyticsBoundary({ children }: { children: ReactNode }) {
    const [analyticsProvider] = useState(
        () =>
            new PostHogAnalyticsProvider(APP_NAME, {
                apiKey: import.meta.env.VITE_POSTHOG_API_KEY,
                host: import.meta.env.VITE_POSTHOG_HOST,
            }),
    );
    const [errorTracker] = useState(() => getOrCreateSentryTracker());

    return (
        <AnalyticsProvider
            provider={analyticsProvider}
            featureFlags={clientApiFeatureFlagsProvider}
            errorTracker={errorTracker}
        >
            <PageTracker />
            {children}
        </AnalyticsProvider>
    );
}
